import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from '@/ui/Modal/Modal';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { SlideGroupsMap } from '@/features/_data/useUnitData';
import { MagicModel } from '@/types/MagicModel';
import { useReadScripts } from '@/features/_data/useScripts';
import checkScriptHomeTimer from '@/utils/checkScriptHomeTimer';
import { Input } from '@/ui/Input/Input';
import { FrontendSlide } from '@/types/SlideType';
import {
  StyledFlex,
  StyledSettingRow,
  StyledSettingTitle,
} from './SettingsModal';
import { EditorSlideComponent } from './EditorSlideComponent';

interface HomescreenSettingsProps {
  readonly unitId: string | undefined;
  readonly homeTimer: number;
  readonly setHomeTimer: React.Dispatch<React.SetStateAction<number>>;
  readonly homeGroupId: string;
  readonly slideGroupsState: SlideGroupsMap;
  readonly setAreHomeSettingsOpen: (isOpen: boolean) => void;
  readonly homeSlideId: string | null;
  readonly handleSelectHomeSlide: (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
  ) => void;
  readonly onChangeNameInLibrary: (slideId: string, newName: string) => void;
  readonly channel: boolean;
  readonly images: FrontendSlide[];
  readonly videos: FrontendSlide[];
  readonly webslides: FrontendSlide[];
  readonly disallowSlideDeletion: boolean;
  readonly languageSupport: boolean;
  readonly setVideos: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setImages: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setWebslides: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly magicModels: MagicModel[];
}

export const HomescreenSettingsModal = ({
  unitId,
  homeTimer,
  setHomeTimer,
  homeGroupId,
  slideGroupsState,
  setAreHomeSettingsOpen,
  homeSlideId,
  handleSelectHomeSlide,
  disallowSlideDeletion,
  images,
  videos,
  setImages,
  setVideos,
  webslides,
  setWebslides,
  channel,
  languageSupport,
  magicModels,
  onChangeNameInLibrary,
}: HomescreenSettingsProps) => {
  // Get all available slides
  const intl = useIntl();
  const { data: script, isLoading } = useReadScripts(unitId || '');
  const [disableHomeTimer, setDisableHomeTimer] = useState(true);

  useEffect(() => {
    if (isLoading) {
      setDisableHomeTimer(true);
      return;
    }
    setDisableHomeTimer(checkScriptHomeTimer(script?.script));
  }, [script]);
  let homeSlide = null;
  if (homeSlideId && slideGroupsState[homeGroupId]) {
    homeSlide = slideGroupsState[homeGroupId].slides[homeSlideId];
  }

  return (
    <Modal
      title="Homescreen Settings"
      onClose={() => setAreHomeSettingsOpen(false)}
    >
      <Flex
        gap="32px 32px"
        padding={16}
        direction="column"
        align="center"
        grow="1"
      >
        <Flex gap="32px 32px" justify="space-between">
          <div>
            <T h3>Home Slide</T>
            <EditorSlideComponent
              onChangeNameInLibrary={onChangeNameInLibrary}
              disallowSlideDeletion={disallowSlideDeletion}
              images={images}
              videos={videos}
              setImages={setImages}
              setVideos={setVideos}
              webslides={webslides}
              setWebslides={setWebslides}
              channelSelective={channel}
              languageSupport={languageSupport}
              magicModels={magicModels}
              isFirst={false}
              isLast={false}
              onClick={() => {}}
              onUpdateSlide={(slideId, slideData, isFromLibrary) => {
                handleSelectHomeSlide(slideId, slideData, isFromLibrary);
              }}
              slide={homeSlide}
              slideGroupId={homeGroupId}
              isTrihowPad
              onUpdateSlideOrder={() => {}}
              onVideoOptionsChange={() => {}}
            />
          </div>
        </Flex>
        <StyledFlex style={{ flexDirection: 'column', gap: '8px' }}>
          <StyledSettingRow enabled={false}>
            <StyledSettingTitle>
              {intl.formatMessage({
                defaultMessage: 'Home Timer (seconds)',
                id: 'Aa9tMV',
              })}
              :
            </StyledSettingTitle>
            {isLoading ? (
              intl.formatMessage({
                defaultMessage: 'checking script...',
                id: 'kIXmES',
              })
            ) : (
              <Input
                testId="home-timer"
                disabled={disableHomeTimer}
                type="number"
                value={String(homeTimer / 1000)}
                onChange={(e) => setHomeTimer(Number(e) * 1000)}
              />
            )}
          </StyledSettingRow>
          {!isLoading && disableHomeTimer && (
            <T>
              {intl.formatMessage({
                defaultMessage:
                  'The home timer feature is currently disabled. To enable this feature, please contact Trihow Support.',
                id: 'GVU1SY',
              })}
            </T>
          )}
        </StyledFlex>
      </Flex>
    </Modal>
  );
};
